import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import { WizardLayout } from "@/components/we360/WizardLayout";
import DemoFormOverlay from "@/components/DemoFormOverlay";
import { useFormValidation } from "@/hooks/useFormValidation";
import { CheckCircle } from "lucide-react";
import { syncF1, syncF2 } from "@/services/backendSync";
import Step1ParQ1 from "./Step1ParQ1";
import Step2ParQ2 from "./Step2ParQ2";
import Step3Walk from "./Step3Walk";
import Step4Squats from "./Step4Squats";
import Step5PushupsFlex from "./Step5PushupsFlex";
import Step6Summary from "./Step6Summary";
import {
  calculateAge,
  classifyByAgeAndSex,
  getFlexCategory,
  WALK_TABLE,
  SQUAT_TABLE,
  PUSHUP_TABLE,
} from "./helpers";

const STEPS = [
  { id: "parq1", title: "Seguridad (PAR-Q)", sub: "Preguntas 1-4" },
  { id: "parq2", title: "Seguridad (PAR-Q)", sub: "Preguntas 5-7 + Resultado" },
  { id: "walk", title: "Caminata 6 Minutos", sub: "Capacidad aeróbica" },
  { id: "squats", title: "Sentadillas 30s", sub: "Fuerza muscular" },
  { id: "pushups", title: "Flexiones + Flexibilidad", sub: "Fuerza y movilidad" },
  { id: "summary", title: "Confirmación", sub: "Revisa tus resultados" },
];

function getStepRules(step, f2Data) {
  switch (step) {
    case 0: {
      const rules = {};
      for (let i = 0; i < 4; i++) {
        rules[`parq_${i}`] = {
          required: true,
          custom: () => f2Data.parqResponses[i] === null ? "Responde esta pregunta" : null,
        };
      }
      return rules;
    }
    case 1: {
      const rules = {};
      for (let i = 4; i < 7; i++) {
        rules[`parq_${i}`] = {
          required: true,
          custom: () => f2Data.parqResponses[i] === null ? "Responde esta pregunta" : null,
        };
      }
      return rules;
    }
    case 2: return {
      caminata6min: { required: true, min: 0, max: 1000, message: "Ingresa los metros recorridos (0-1000)" },
    };
    case 3: return {
      sentadillas: { required: true, min: 0, max: 60, message: "Ingresa las repeticiones (0-60)" },
    };
    case 4: return {
      flexiones: { required: true, min: 0, max: 100, message: "Ingresa las repeticiones (0-100)" },
      flexibilidad: { required: true, message: "Selecciona tu nivel de flexibilidad" },
    };
    case 5: return {
      confirmacion: {
        required: true,
        custom: (v) => v !== true ? "Debes confirmar para enviar" : null,
      },
    };
    default: return {};
  }
}

export default function FormF2() {
  const { profile, setProfile, isDemo } = useUserProfile();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [fade, setFade] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const { validateStep, validateField, getError, isStepValid, clearErrors, onFieldChange } = useFormValidation();

  const [f2Data, setF2Data] = useState({
    parqResponses: [null, null, null, null, null, null, null],
    parqOverride: false,
    parqMedsConfirmed: false,
    caminata6min: "",
    sentadillas: "",
    flexiones: "",
    flexibilidad: "",
    confirmacion: false,
  });

  const rules = useMemo(() => getStepRules(step, f2Data), [step, f2Data]);

  const age = calculateAge(profile.fechaNacimiento);
  const sex = profile.sexo || profile.genero;
  const isDefaultSex = sex !== "Masculino" && sex !== "Femenino";

  const setF2Field = useCallback((key, value) => {
    setF2Data((prev) => ({ ...prev, [key]: value }));
    // Clear validation errors in real-time
    const rule = rules[key];
    if (rule) onFieldChange(key, value, rule);
  }, [rules, onFieldChange]);

  const setParqResponse = useCallback((index, value) => {
    setF2Data((prev) => {
      const newResponses = [...prev.parqResponses];
      newResponses[index] = value;
      const nextState = { ...prev, parqResponses: newResponses };
      if (index === 5 && value === true) {
        nextState.parqMedsConfirmed = false;
      }
      return nextState;
    });
  }, []);

  // PAR-Q computed
  const allParqAnswered = f2Data.parqResponses.every((r) => r !== null);
  const parqAlerta = f2Data.parqResponses.some((r) => r === true);
  const parqRiskCount = f2Data.parqResponses.filter((r) => r === true).length;
  const hasF1Meds = Array.isArray(profile.medicamentos) && profile.medicamentos.length > 0;
  const needsParqMedsConfirm = hasF1Meds && f2Data.parqResponses[5] === false && !f2Data.parqMedsConfirmed;

  // Classifications
  const walkLabels = ["Bajo", "Normal", "Bueno", "Excelente"];
  const testLabels = ["Bajo", "Promedio", "Bueno", "Excelente"];
  const walkClass = classifyByAgeAndSex(f2Data.caminata6min, sex, age, WALK_TABLE, walkLabels);
  const squatClass = classifyByAgeAndSex(f2Data.sentadillas, sex, age, SQUAT_TABLE, testLabels);
  const pushupClass = classifyByAgeAndSex(f2Data.flexiones, sex, age, PUSHUP_TABLE, testLabels);
  const flexCategory = getFlexCategory(f2Data.flexibilidad);

  // Build flat data for validation
  const flatData = useMemo(() => {
    const d = { ...f2Data };
    f2Data.parqResponses.forEach((r, i) => { d[`parq_${i}`] = r; });
    return d;
  }, [f2Data]);

  // Navigation
  const go = useCallback((dir) => {
    setFade(false);
    setTimeout(() => { setStep((s) => s + dir); setFade(true); }, 200);
  }, []);

  const goNext = useCallback(() => {
    if (isDemo) { go(1); return; }
    // Step 1 extra: PAR-Q override & F1 medication cross-check logic
    if (step === 1) {
      const errs = validateStep(rules, flatData);
      if (Object.keys(errs).length > 0) return;
      if (needsParqMedsConfirm) return;
      if (allParqAnswered && parqAlerta && !f2Data.parqOverride) return;
    } else {
      const errs = validateStep(rules, flatData);
      if (Object.keys(errs).length > 0) return;
    }
    go(1);
  }, [step, rules, flatData, validateStep, go, allParqAnswered, parqAlerta, f2Data.parqOverride, needsParqMedsConfirm, isDemo]);

  const goBack = useCallback(() => { clearErrors(); go(-1); }, [go, clearErrors]);

  const handleSubmit = useCallback(async () => {
    if (isDemo) return;
    const errs = validateStep(rules, flatData);
    if (Object.keys(errs).length > 0) return;
    if (!f2Data.confirmacion) return;
    const now = new Date().toISOString();
    const clearedF1Meds = Boolean(hasF1Meds && f2Data.parqResponses[5] === false && f2Data.parqMedsConfirmed);
    const f2Results = {
      parqResponses: f2Data.parqResponses,
      parqAlerta,
      parqRiskCount,
      parqOverride: f2Data.parqOverride,
      caminata6min: parseFloat(f2Data.caminata6min) || 0,
      clasificacion6mwt: walkClass?.label || "",
      sentadillas: parseInt(f2Data.sentadillas) || 0,
      clasificacionSentadillas: squatClass?.label || "",
      flexiones: parseInt(f2Data.flexiones) || 0,
      clasificacionFlexiones: pushupClass?.label || "",
      flexibilidad: f2Data.flexibilidad,
      clasificacionFlexibilidad: flexCategory?.category || "",
      confirmacion: true,
      createdAt: now,
      completedAt: now,
    };
    setSubmitted(true);
    await setProfile((prev) => ({
      ...prev,
      ...(clearedF1Meds
        ? {
            usaMedicamentos: "No",
            medicamentos: [],
            medicationsConfirmedEmpty: true,
            pendingFhirF1: true,
          }
        : {}),
      f2Results,
      fhirSyncStatus: "PENDING_FHIR",
      pendingFhirF2: true,
    }));

    const email = user?.email || profile.email;
    if (email) {
      Promise.all([
        syncF2(f2Results, email),
        clearedF1Meds
          ? syncF1(
              {
                ...profile,
                usaMedicamentos: "No",
                medicamentos: [],
                medicationsConfirmedEmpty: true,
                f2Results,
              },
              email
            )
          : Promise.resolve({ ok: true }),
      ]).then(([f2Res, f1Res]) => {
        if (f2Res?.ok && f1Res?.ok) {
          console.log("[F2] Backend sync OK:", f2Res.data);
          setProfile((prev) => ({
            ...prev,
            pendingFhirF2: false,
            ...(clearedF1Meds ? { pendingFhirF1: false } : {}),
            fhirSyncStatus: "SYNCED",
            fhirSyncedAt: new Date().toISOString(),
          }));
        } else {
          console.warn("[F2] Backend sync pending Outbox retry:", { f2Res, f1Res });
        }
      });
    }

    setTimeout(() => navigate("/dashboard"), 2000);
  }, [f2Data, flatData, rules, parqAlerta, parqRiskCount, walkClass, squatClass, pushupClass, flexCategory, setProfile, navigate, validateStep, isDemo, user, profile, hasF1Meds]);

  // For step-level disabled
  const nextDisabledBase = !isDemo && !isStepValid(rules, flatData);
  const nextDisabled = step === 1
    ? nextDisabledBase || needsParqMedsConfirm || (allParqAnswered && parqAlerta && !f2Data.parqOverride)
    : nextDisabledBase;

  const handleBlur = useCallback((field) => {
    if (isDemo) return;
    const rule = rules[field];
    if (rule) validateField(field, flatData[field], rule);
  }, [rules, flatData, validateField, isDemo]);

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 font-sans" style={{ background: "#FCFAEE" }}>
        <div className="we-card p-8 text-center max-w-[380px] w-full" style={{ boxShadow: "0 8px 30px rgba(45,42,36,0.10)" }} data-testid="f2-success-card">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "#E2F0E8" }}>
            <CheckCircle size={32} color="#157346" />
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: "#2D2A24" }}>¡Evaluación completada!</h2>
          <p className="text-sm" style={{ color: "#71644C" }}>Redirigiendo a tu dashboard...</p>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    switch (step) {
      case 0: return <Step1ParQ1 userName={profile.nombres} age={age} sex={sex} parqResponses={f2Data.parqResponses} setParqResponse={setParqResponse} errors={{ getError }} />;
      case 1: return <Step2ParQ2 parqResponses={f2Data.parqResponses} setParqResponse={setParqResponse} parqOverride={f2Data.parqOverride} setParqOverride={(v) => setF2Field("parqOverride", v)} allParqAnswered={allParqAnswered} parqAlerta={parqAlerta} parqRiskCount={parqRiskCount} medicamentos={profile.medicamentos || []} parqMedsConfirmed={f2Data.parqMedsConfirmed} setParqMedsConfirmed={(v) => setF2Field("parqMedsConfirmed", v)} errors={{ getError }} />;
      case 2: return <Step3Walk value={f2Data.caminata6min} onChange={(v) => setF2Field("caminata6min", v)} classification={walkClass} sex={sex} isDefaultSex={isDefaultSex && !!walkClass} errors={{ getError }} onBlur={handleBlur} />;
      case 3: return <Step4Squats value={f2Data.sentadillas} onChange={(v) => setF2Field("sentadillas", v)} classification={squatClass} sex={sex} isDefaultSex={isDefaultSex && !!squatClass} errors={{ getError }} onBlur={handleBlur} />;
      case 4: return <Step5PushupsFlex flexiones={f2Data.flexiones} onFlexionesChange={(v) => setF2Field("flexiones", v)} pushupClass={pushupClass} flexibilidad={f2Data.flexibilidad} onFlexibilidadChange={(v) => setF2Field("flexibilidad", v)} sex={sex} isDefaultSex={isDefaultSex && !!pushupClass} errors={{ getError }} onBlur={handleBlur} />;
      case 5: return <Step6Summary f2Data={f2Data} walkClass={walkClass} squatClass={squatClass} pushupClass={pushupClass} flexCategory={flexCategory} parqAlerta={parqAlerta} parqRiskCount={parqRiskCount} confirmacion={f2Data.confirmacion} setConfirmacion={(v) => setF2Field("confirmacion", v)} errors={{ getError }} />;
      default: return null;
    }
  };

  return (
    <DemoFormOverlay>
      <WizardLayout
        steps={STEPS}
        currentStep={step}
        onNext={goNext}
        onBack={goBack}
        fade={fade}
        userName={profile.nombres}
        isLastStep={step === 5}
        onSubmit={handleSubmit}
        submitDisabled={!f2Data.confirmacion || isDemo}
        nextDisabled={nextDisabled}
      >
        {renderStep()}
      </WizardLayout>
    </DemoFormOverlay>
  );
}
